// Core
import { ReactNode, useCallback, useEffect, useLayoutEffect, useMemo, useState } from 'react';
// Packages
import { ConfigProvider } from 'antd';
// Components
import Empty from 'components/lib/Empty';
// Theme
import { ThemeContext } from './ThemeContext';
import { ANTD_ICON_CONFIG } from './antdIcons';
import { buildAntdTheme } from './antdTheme';
import { injectTokenStyles } from './injectTokenStyles';
import { readStoredMode, storeMode, subscribeToStoredMode } from './themeStorage';
import { usePrefersDark } from './usePrefersDark';
import { usePrefersReducedMotion } from './usePrefersReducedMotion';
import { useThemeCrossFade } from './useThemeCrossFade';
import { ResolvedTheme, THEME_ATTRIBUTE, THEME_MODE, ThemeMode } from './constants';

interface IProps {
  children: ReactNode;
}

const resolveTheme = (mode: ThemeMode, prefersDark: boolean): ResolvedTheme => {
  if (mode === THEME_MODE.SYSTEM) {
    return prefersDark ? THEME_MODE.DARK : THEME_MODE.LIGHT;
  }

  return mode;
};

// Tables and lists show the lib empty state (Lucide glyph) like the rest of the app; selects and
// the other components keep antd's own (null falls back to it).
const renderEmpty = (componentName?: string) =>
  componentName === 'Table' || componentName === 'List' ? <Empty noDataType="simple" /> : null;

const ThemeProvider = ({ children }: IProps) => {
  const [mode, setMode] = useState<ThemeMode>(readStoredMode);
  const prefersDark = usePrefersDark();
  const isReducedMotion = usePrefersReducedMotion();

  const resolvedTheme = resolveTheme(mode, prefersDark);
  const antdTheme = useMemo(
    () => buildAntdTheme(resolvedTheme, { isReducedMotion }),
    [resolvedTheme, isReducedMotion],
  );

  useThemeCrossFade(resolvedTheme, isReducedMotion);

  // A theme picked in another browser tab applies here too; without it the two tabs disagree until reload.
  useEffect(() => subscribeToStoredMode(setMode), []);

  // A layout effect, so the attribute is on <html> before the first paint and a dark user never
  // sees a light frame.
  useLayoutEffect(() => {
    injectTokenStyles();
    document.documentElement.setAttribute(THEME_ATTRIBUTE, resolvedTheme);
    // antd's static helpers (Modal.confirm, message, notification) render outside this provider.
    ConfigProvider.config({ theme: antdTheme });
  }, [resolvedTheme, antdTheme]);

  const changeMode = useCallback((nextMode: ThemeMode) => {
    setMode(nextMode);
    storeMode(nextMode);
  }, []);

  const value = useMemo(
    () => ({ mode, resolvedTheme, setMode: changeMode }),
    [mode, resolvedTheme, changeMode],
  );

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={antdTheme} renderEmpty={renderEmpty} {...ANTD_ICON_CONFIG}>
        {children}
      </ConfigProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeProvider;
